/**
 * AdminGamesSelectionsPage — V11 (JP 2027).
 *
 * Queue de validation des sélections déclarées par les Athletes.
 * PENDING → SELECTED (Valider) ou REJECTED (Refuser). SELECTED → REVOKED (Révoquer).
 * Réservé aux rôles admin & copf.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { showError } from "@/lib/errors";
import { resolveAthletePhoto } from "@/lib/photoFallback";
import { toast } from "sonner";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  ShieldAlert, ShieldCheck, ArrowLeft, Check, X, RotateCcw, Loader2, ExternalLink,
  Flag, RefreshCw, Search,
} from "lucide-react";
import { Link } from "react-router-dom";

const STATUSES = [
  { v: "pending", label: "En attente", tone: "amber" },
  { v: "selected", label: "Officielles", tone: "gold" },
  { v: "rejected", label: "Refusées", tone: "red" },
  { v: "revoked", label: "Révoquées", tone: "slate" },
  { v: "all", label: "Tous les athlètes", tone: "sky" }, // V13.7 — fusion : sélection en masse
];

export default function AdminGamesSelectionsPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const isAdmin = user && (user.roles || []).some((r) => ["admin", "copf"].includes(r));

  const [status, setStatus] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null); // { kind: 'validate'|'reject'|'revoke', item }
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  // V13.7 — état pour l'onglet "Tous les athlètes" (fusion de l'ex page Route JP 2027)
  const [allAthletes, setAllAthletes] = useState([]);
  const [allLoading, setAllLoading] = useState(false);
  const [allBusyId, setAllBusyId] = useState(null);
  const [allQuery, setAllQuery] = useState("");
  const [allFilter, setAllFilter] = useState("all"); // all | selected | not-selected
  const [allDiscipline, setAllDiscipline] = useState("__all__");

  const fetchList = useCallback(async () => {
    if (!isAdmin || status === "all") return;
    setLoading(true);
    try {
      const { data } = await api.get("/games-selections/queue", { params: { status } });
      setItems(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Sélections JP 2027 :" });
    } finally {
      setLoading(false);
    }
  }, [isAdmin, status]);

  // V13.7 — charge la liste complète des athlètes + les sélections officielles
  // pour marquer qui est actuellement Selected. Réutilise les endpoints existants
  // (aucune nouvelle route backend).
  const fetchAllAthletes = useCallback(async () => {
    if (!isAdmin) return;
    setAllLoading(true);
    try {
      // V13.11 — endpoint admin dédié (retourne TOUS les athlètes avec leur
      // statut de sélection, sans être plafonné à 200 ni filtrer les brouillons)
      const { data } = await api.get("/games-selections/all-athletes", { params: { limit: 2000 } });
      setAllAthletes(data.items || []);
    } catch (e) {
      showError(e, { prefix: "Chargement athlètes :" });
    } finally {
      setAllLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => { fetchList(); }, [fetchList]);
  // V13.7 — Charge la liste complète des athlètes quand on ouvre l'onglet dédié.
  useEffect(() => { if (status === "all") fetchAllAthletes(); }, [status, fetchAllAthletes]);

  // V13.7 — Bascule officielle / retrait via les endpoints existants
  // `POST /games-selections/admin/create` et `PATCH /{id}/revoke`.
  const toggleOfficial = async (athlete) => {
    setAllBusyId(athlete.id);
    try {
      if (!athlete.is_selected) {
        const { data } = await api.post("/games-selections/admin/create", {
          athlete_id: athlete.id,
          discipline: athlete.main_discipline,
          source: "admin_direct",
        });
        setAllAthletes((list) => list.map((x) => x.id === athlete.id
          ? { ...x, is_selected: true, selection: data.selection }
          : x));
        toast.success(`${athlete.full_name} — officialisé(e) JP 2027`);
        // Rafraîchir aussi la queue "selected" côté état si nécessaire
        if (status === "selected") await fetchList();
      } else {
        if (!athlete.selection?.id) throw new Error("Sélection introuvable");
        await api.patch(`/games-selections/${athlete.selection.id}/revoke`, { reason: "admin_ui_toggle" });
        setAllAthletes((list) => list.map((x) => x.id === athlete.id
          ? { ...x, is_selected: false, selection: null }
          : x));
        toast.success(`${athlete.full_name} — retiré(e) de la sélection`);
        if (status === "selected") await fetchList();
      }
    } catch (e) {
      showError(e, { prefix: "Sélection :" });
    } finally {
      setAllBusyId(null);
    }
  };

  // Disciplines uniques pour le filtre de l'onglet "Tous"
  const allDisciplines = useMemo(() => {
    const s = new Set();
    for (const a of allAthletes) if (a.main_discipline) s.add(a.main_discipline);
    return Array.from(s).sort();
  }, [allAthletes]);

  const filteredAll = useMemo(() => {
    let list = allAthletes;
    if (allFilter === "selected") list = list.filter((a) => a.is_selected);
    else if (allFilter === "not-selected") list = list.filter((a) => !a.is_selected);
    if (allDiscipline !== "__all__") list = list.filter((a) => a.main_discipline === allDiscipline);
    if (allQuery.trim()) {
      const q = allQuery.toLowerCase();
      list = list.filter((a) =>
        (a.full_name || "").toLowerCase().includes(q) ||
        (a.slug || "").toLowerCase().includes(q) ||
        (a.island || "").toLowerCase().includes(q)
      );
    }
    return [...list].sort((a, b) => {
      if (a.is_selected !== b.is_selected) return a.is_selected ? -1 : 1;
      return (a.full_name || "").localeCompare(b.full_name || "");
    });
  }, [allAthletes, allQuery, allFilter, allDiscipline]);

  const allStats = useMemo(() => ({
    selected: allAthletes.filter((a) => a.is_selected).length,
    total: allAthletes.length,
  }), [allAthletes]);

  const submit = async () => {
    if (!action) return;
    setBusy(true);
    try {
      await api.patch(`/games-selections/${action.item.id}/${action.kind}`, { reason: reason.trim() || null });
      toast.success(
        action.kind === "validate" ? "Sélection validée."
        : action.kind === "reject" ? "Sélection refusée."
        : "Sélection révoquée."
      );
      setAction(null);
      setReason("");
      await fetchList();
    } catch (e) {
      showError(e, { prefix: "Action :" });
    } finally {
      setBusy(false);
    }
  };

  if (!user) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="games-sel-need-login">
        <Card className="p-6 text-center">Connecte-toi pour accéder à cette page.</Card>
      </div>
    );
  }
  if (!isAdmin) {
    return (
      <div className="mx-auto max-w-3xl p-8" data-testid="games-sel-forbidden">
        <Card className="p-6">
          <ShieldAlert className="h-6 w-6 text-[hsl(var(--aito-energy))]" />
          <h2 className="font-display text-xl font-bold mt-2">Accès administrateur</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Ton compte n'a pas les droits pour valider les sélections JP 2027.
          </p>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8 py-6 space-y-4" data-testid="admin-games-selections-page">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => nav("/admin")} data-testid="games-sel-back">
              <ArrowLeft className="h-3.5 w-3.5 mr-1" />Admin
            </Button>
            <span>/ Sélections JP 2027</span>
          </div>
          <h1 className="font-display text-2xl md:text-3xl font-bold flex items-center gap-2 mt-1">
            <Flag className="h-7 w-7 text-[hsl(var(--aito-gold))]" />
            Sélections officielles — Jeux du Pacifique 2027
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            File de validation des sélections déclarées. <strong>PENDING</strong> → <strong>SELECTED</strong> (Valider) ou <strong>REJECTED</strong> (Refuser).
            Une <strong>SELECTED</strong> peut être <strong>REVOKED</strong> (résultats historiques conservés).
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchList} disabled={loading} data-testid="games-sel-refresh">
          {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
          Rafraîchir
        </Button>
      </div>

      {/* Tabs par statut */}
      <Tabs value={status} onValueChange={setStatus}>
        <TabsList data-testid="games-sel-tabs">
          {STATUSES.map((s) => (
            <TabsTrigger key={s.v} value={s.v} data-testid={`games-sel-tab-${s.v}`}>{s.label}</TabsTrigger>
          ))}
        </TabsList>

        {STATUSES.map((s) => (
          <TabsContent key={s.v} value={s.v} className="mt-4">
            {s.v === "all" ? (
              <AllAthletesTab
                loading={allLoading}
                onRefresh={fetchAllAthletes}
                items={filteredAll}
                total={allStats.total}
                selectedCount={allStats.selected}
                busyId={allBusyId}
                onToggle={toggleOfficial}
                query={allQuery}
                setQuery={setAllQuery}
                filter={allFilter}
                setFilter={setAllFilter}
                discipline={allDiscipline}
                setDiscipline={setAllDiscipline}
                disciplines={allDisciplines}
              />
            ) : loading ? (
              <div className="space-y-2">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-20 w-full" />)}</div>
            ) : items.length === 0 ? (
              <Card className="p-8 text-center text-sm text-muted-foreground" data-testid={`games-sel-empty-${s.v}`}>
                Aucune sélection <strong>{s.label.toLowerCase()}</strong> pour le moment.
              </Card>
            ) : (
              <div className="grid grid-cols-1 gap-2" data-testid={`games-sel-list-${s.v}`}>
                {items.map((it) => (
                  <SelectionRow
                    key={it.id}
                    item={it}
                    onValidate={() => setAction({ kind: "validate", item: it })}
                    onReject={() => setAction({ kind: "reject", item: it })}
                    onRevoke={() => setAction({ kind: "revoke", item: it })}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Dialog action */}
      {action && (
        <Dialog open={!!action} onOpenChange={(v) => { if (!v) { setAction(null); setReason(""); } }}>
          <DialogContent data-testid="games-sel-dialog">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {action.kind === "validate" && <><Check className="h-5 w-5 text-emerald-600" /> Valider la sélection</>}
                {action.kind === "reject" && <><X className="h-5 w-5 text-destructive" /> Refuser la déclaration</>}
                {action.kind === "revoke" && <><RotateCcw className="h-5 w-5 text-amber-600" /> Révoquer la sélection</>}
              </DialogTitle>
              <DialogDescription>
                {action.item.athlete?.full_name} — {action.item.discipline || "discipline non précisée"}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              {action.kind === "validate" && (
                <p className="text-xs text-muted-foreground">
                  L'athlète sera visible immédiatement dans la délégation Polynésie publique et compté dans les KPI JP 2027.
                </p>
              )}
              {action.kind === "reject" && (
                <p className="text-xs text-muted-foreground">
                  La déclaration passe en <strong>REJECTED</strong>. L'athlète peut redéclarer plus tard si nécessaire.
                </p>
              )}
              {action.kind === "revoke" && (
                <p className="text-xs text-muted-foreground">
                  L'athlète disparaît de la délégation active. Les résultats déjà enregistrés (Achievements) sont <strong>conservés</strong> dans son palmarès.
                </p>
              )}
              <div>
                <label className="text-xs">Motif {action.kind !== "validate" && "(recommandé)"}</label>
                <Textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={2}
                  placeholder={
                    action.kind === "reject" ? "Ex : n'apparaît pas dans la liste officielle fédération."
                    : action.kind === "revoke" ? "Ex : retrait de la délégation confirmée par COPF."
                    : "Optionnel"
                  }
                  data-testid="games-sel-reason"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setAction(null); setReason(""); }} disabled={busy} data-testid="games-sel-cancel">Annuler</Button>
              <Button
                variant={action.kind === "reject" ? "destructive" : "default"}
                onClick={submit}
                disabled={busy}
                data-testid="games-sel-confirm"
              >
                {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                Confirmer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}

function SelectionRow({ item, onValidate, onReject, onRevoke }) {
  const a = item.athlete || {};
  const initials = (a.full_name || item.full_name || "?").split(" ").map((s) => s[0]).join("").slice(0, 2).toUpperCase();
  return (
    <Card className="p-3 flex items-center gap-3" data-testid={`games-sel-row-${item.id}`}>
      <Avatar className="h-12 w-12 shrink-0">
        <AvatarImage src={resolveAthletePhoto(a)} alt={a.full_name} />
        <AvatarFallback className="bg-[hsl(var(--aito-gold)/0.2)] text-[hsl(var(--aito-gold))] font-semibold">{initials}</AvatarFallback>
      </Avatar>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          {a.slug ? (
            <Link to={`/athletes/${a.slug}`} className="font-medium hover:text-[hsl(var(--aito-lagoon))] truncate" data-testid={`games-sel-name-${item.id}`}>
              {a.full_name || item.full_name} <ExternalLink className="inline h-3 w-3 ml-0.5" />
            </Link>
          ) : (
            <span className="font-medium truncate">{a.full_name || item.full_name}</span>
          )}
          <Badge variant="outline" className="text-[9px] capitalize">{item.discipline || a.main_discipline || "—"}</Badge>
          {item.source && <Badge variant="secondary" className="text-[9px]">{item.source.replace(/_/g, " ")}</Badge>}
        </div>
        <div className="text-[11px] text-muted-foreground mt-0.5 flex flex-wrap gap-2">
          {a.club_name && <span>Club : {a.club_name}</span>}
          {a.federation && <span>Féd : {a.federation}</span>}
          {a.island && <span>Île : {a.island}</span>}
          {item.declared_at && <span>Déclarée : {new Date(item.declared_at).toLocaleDateString("fr-FR")}</span>}
          {item.validated_at && <span>Validée : {new Date(item.validated_at).toLocaleDateString("fr-FR")}</span>}
          {item.rejected_at && <span>Refusée : {new Date(item.rejected_at).toLocaleDateString("fr-FR")}</span>}
          {item.revoked_at && <span>Révoquée : {new Date(item.revoked_at).toLocaleDateString("fr-FR")}</span>}
        </div>
        {item.rejected_reason && <p className="text-[11px] text-destructive mt-1">Motif refus : {item.rejected_reason}</p>}
        {item.revoked_reason && <p className="text-[11px] text-amber-700 mt-1">Motif révocation : {item.revoked_reason}</p>}
      </div>
      <div className="flex gap-1 shrink-0">
        {item.status === "pending" && (
          <>
            <Button size="sm" onClick={onValidate} data-testid={`games-sel-validate-${item.id}`}>
              <Check className="h-3.5 w-3.5 mr-1" />Valider
            </Button>
            <Button size="sm" variant="outline" onClick={onReject} className="text-destructive hover:text-destructive" data-testid={`games-sel-reject-${item.id}`}>
              <X className="h-3.5 w-3.5 mr-1" />Refuser
            </Button>
          </>
        )}
        {item.status === "selected" && (
          <Button size="sm" variant="outline" onClick={onRevoke} className="text-amber-700 hover:text-amber-700" data-testid={`games-sel-revoke-${item.id}`}>
            <RotateCcw className="h-3.5 w-3.5 mr-1" />Révoquer
          </Button>
        )}
        {(item.status === "rejected" || item.status === "revoked") && (
          <Badge variant="outline" className="text-[10px]">{item.status}</Badge>
        )}
      </div>
    </Card>
  );
}

// V13.7 — Onglet "Tous les athlètes" (fusion de l'ex page /admin/jp2027-selection).
// Permet à l'admin/COPF de basculer directement n'importe quel athlète du site
// en Sélection Officielle JP 2027, sans passer par une déclaration préalable.
function AllAthletesTab({
  loading, onRefresh, items, total, selectedCount, busyId, onToggle,
  query, setQuery, filter, setFilter, discipline, setDiscipline, disciplines,
}) {
  return (
    <div data-testid="games-sel-all-tab">
      <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
        <p className="text-xs text-muted-foreground">
          Bascule n'importe quel athlète en <strong>Sélection Officielle</strong> — le badge apparaît
          immédiatement sur sa fiche publique, dans la page publique JP 2027 et sur son PDF.
        </p>
        <div className="flex items-center gap-2">
          <Badge className="bg-[hsl(var(--aito-gold))] text-black text-xs px-3 py-1" data-testid="all-selected-count">
            {selectedCount}/{total} sélectionné{selectedCount > 1 ? "s" : ""}
          </Badge>
          <Button variant="outline" size="sm" onClick={onRefresh} disabled={loading} data-testid="games-sel-all-refresh">
            {loading ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5 mr-1.5" />}
            Rafraîchir
          </Button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mb-4">
        <div className="relative sm:col-span-1">
          <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="games-sel-all-search"
            placeholder="Rechercher un nom, île, slug…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
          />
        </div>
        <Select value={filter} onValueChange={setFilter}>
          <SelectTrigger data-testid="games-sel-all-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tous les athlètes</SelectItem>
            <SelectItem value="selected">Officialisés</SelectItem>
            <SelectItem value="not-selected">Non officialisés</SelectItem>
          </SelectContent>
        </Select>
        <Select value={discipline} onValueChange={setDiscipline}>
          <SelectTrigger data-testid="games-sel-all-discipline"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">Toutes disciplines</SelectItem>
            {disciplines.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-14 w-full" />)}</div>
      ) : items.length === 0 ? (
        <Card className="p-8 text-center text-sm text-muted-foreground border-dashed">
          Aucun athlète ne correspond aux filtres.
        </Card>
      ) : (
        <Card className="divide-y" data-testid="games-sel-all-list">
          {items.map((a) => (
            <div key={a.id} className="flex items-center gap-3 p-3 hover:bg-muted/30 transition-colors" data-testid={`all-row-${a.slug || a.id}`}>
              <img
                src={resolveAthletePhoto(a)}
                alt=""
                className="h-10 w-10 rounded-full object-cover object-top border shrink-0"
                loading="lazy"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/athletes/${a.slug || a.id}`} target="_blank" rel="noreferrer" className="font-semibold truncate hover:text-[hsl(var(--aito-lagoon))]">
                    {a.full_name} <ExternalLink className="inline h-3 w-3 ml-0.5" />
                  </Link>
                  {a.is_selected && (
                    <Badge className="bg-[hsl(var(--aito-gold))] text-black text-[10px]">
                      <Flag className="h-3 w-3 mr-1" /> JP 2027
                    </Badge>
                  )}
                  {a.is_selected && a.selection?.source && (
                    <Badge variant="outline" className="text-[10px]" title="Source de la sélection">
                      {a.selection.source.replace(/_/g, " ")}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-muted-foreground truncate">
                  {[a.main_discipline, a.island, a.commune].filter(Boolean).join(" · ")}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs font-semibold ${a.is_selected ? "text-[hsl(var(--aito-gold))]" : "text-muted-foreground"}`}>
                  {a.is_selected ? "Sélectionné" : "Non sélectionné"}
                </span>
                {busyId === a.id ? (
                  <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
                ) : (
                  <Switch
                    checked={!!a.is_selected}
                    onCheckedChange={() => onToggle(a)}
                    data-testid={`all-switch-${a.slug || a.id}`}
                    aria-label={`Sélection officielle JP 2027 ${a.full_name}`}
                  />
                )}
              </div>
            </div>
          ))}
        </Card>
      )}

      <p className="mt-3 text-[11px] text-muted-foreground">
        Bascule = <code>POST /games-selections/admin/create</code> (SELECTED direct) ou
        <code>PATCH /revoke</code>. Le flag <code>road_to_2027</code> est automatiquement synchronisé.
      </p>
    </div>
  );
}

